/**
 * A formatação que o WhatsApp usa, lida de volta.
 *
 * O cliente escreve `*negrito*`, `_itálico_`, `~riscado~` e `` `mono` ``, e o telefone dele mostra
 * formatado. No SAC isso chegava como os próprios asteriscos no meio do texto — e, pior, as
 * quebras de linha sumiam: 838 mensagens em produção têm quebra de linha, e o HTML as colapsa.
 * Uma lista de medidas virava um parágrafo corrido, e a Helena ia responder pelo WhatsApp Web.
 *
 * Só a ÁRVORE mora aqui, sem JSX: assim a regra de onde um marcador vale é testável pela suíte
 * do servidor, que é onde os testes deste repositório rodam. Quem desenha é a web.
 *
 * As regras são as do WhatsApp, e a que mais importa é a última: um marcador colado em espaço
 * não formata nada. Sem ela, "2 * 3 * 4" vira itálico e o cliente lê uma conta errada.
 */
const MARCADORES = [
    { mark: '*', type: 'bold' },
    { mark: '_', type: 'italic' },
    { mark: '~', type: 'strike' },
    { mark: '`', type: 'mono' },
];
/** Espaço de qualquer tipo, incluindo a quebra de linha — nenhum deles pode encostar no marcador. */
const ESPACO = /\s/;
/**
 * Letra ou número, em qualquer alfabeto. É o que define "dentro de uma palavra".
 *
 * Um marcador só vale numa BORDA de palavra, e a primeira versão disto só conferia o lado de
 * dentro. Medido nas 43 mensagens reais com marcador: três URLs viravam itálico
 * (`matt_tool=...&matt_word=`, `?_gl=1*1vgmgtp*`) e dois relatórios colados viravam riscado,
 * porque o "~" de "aproximadamente" aparecia duas vezes (`~09:40` … `~21/09`). O WhatsApp Web
 * não formata nenhum deles.
 */
const PALAVRA = /[\p{L}\p{N}]/u;
export function parseWhatsAppMarkup(text) {
    if (!text)
        return [];
    return parse(text, 0);
}
/**
 * `profundidade` existe só para impedir recursão infinita num texto adversário. O WhatsApp
 * aninha (negrito dentro de itálico), mas ninguém aninha cinco níveis de verdade.
 */
function parse(text, profundidade) {
    const out = [];
    let buffer = '';
    let i = 0;
    const despejar = () => {
        if (buffer) {
            out.push({ type: 'text', value: buffer });
            buffer = '';
        }
    };
    while (i < text.length) {
        const ch = text[i];
        const m = profundidade < 4 ? MARCADORES.find((x) => x.mark === ch) : undefined;
        // Abre só no começo do texto ou depois de um espaço. `?_gl=` e `matt_word=` não abrem nada —
        // e é isso que mantém a formatação fora das URLs, que era metade dos falsos positivos.
        const podeAbrir = i === 0 || ESPACO.test(text[i - 1]);
        if (m && podeAbrir) {
            // Bloco de código: ```assim```, que o WhatsApp trata como um marcador só.
            const triplo = m.mark === '`' && text.startsWith('```', i);
            const abre = triplo ? '```' : m.mark;
            const fim = acharFechamento(text, i + abre.length, abre);
            if (fim !== -1) {
                const dentro = text.slice(i + abre.length, fim);
                despejar();
                out.push({
                    type: m.type,
                    // Monoespaçado é literal: um asterisco dentro de código é um asterisco.
                    children: m.type === 'mono' ? [{ type: 'text', value: dentro }] : parse(dentro, profundidade + 1),
                });
                i = fim + abre.length;
                continue;
            }
        }
        buffer += ch;
        i++;
    }
    despejar();
    return out;
}
/**
 * Onde o marcador fecha — ou -1 quando ele não fecha, e aí é só um caractere comum.
 *
 * Três condições, e as três vêm do WhatsApp: o conteúdo não pode começar nem terminar em espaço,
 * não pode ser vazio, e o que vem DEPOIS do marcador de fechamento não pode ser letra nem número.
 * A terceira é o que impede `~21/09` de fechar um `~` aberto lá atrás — pontuação e espaço
 * fecham, `~2` não.
 */
function acharFechamento(text, inicio, marcador) {
    if (inicio >= text.length || ESPACO.test(text[inicio]))
        return -1;
    let i = inicio;
    while (i < text.length) {
        const achou = text.indexOf(marcador, i);
        if (achou === -1)
            return -1;
        if (achou === inicio)
            return -1; // conteúdo vazio: `**` não é negrito
        const depois = text[achou + marcador.length];
        if (!ESPACO.test(text[achou - 1]) && (depois === undefined || !PALAVRA.test(depois)))
            return achou;
        i = achou + marcador.length;
    }
    return -1;
}
/** O texto sem os marcadores — para prévia na lista, busca e qualquer lugar sem formatação. */
export function stripWhatsAppMarkup(text) {
    const achatar = (ns) => ns.map((n) => (n.type === 'text' ? n.value : achatar(n.children))).join('');
    return achatar(parseWhatsAppMarkup(text));
}
//# sourceMappingURL=whatsappMarkup.js.map